import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import TurnstileChallenge from './components/TurnstileChallenge';
import MainLayout from './layouts/MainLayout';
import Login from './pages/Login';
import Landing from './pages/Landing';
import ChangePassword from './pages/ChangePassword';
import Dashboard from './pages/Dashboard';

import './App.css';
import './styles/common.css';

// Lazy-loaded pages (code splitting)
const CustomerList = React.lazy(() => import('./pages/customers/CustomerList'));
const CustomerForm = React.lazy(() => import('./pages/customers/CustomerForm'));
const CustomerDetail = React.lazy(() => import('./pages/customers/CustomerDetail'));
const ServiceList = React.lazy(() => import('./pages/services/ServiceList'));
const ServiceForm = React.lazy(() => import('./pages/services/ServiceForm'));
const PaymentList = React.lazy(() => import('./pages/payments/PaymentList'));
const InventoryList = React.lazy(() => import('./pages/inventory/InventoryList'));
const Reports = React.lazy(() => import('./pages/reports/Reports'));
const UserList = React.lazy(() => import('./pages/users/UserList'));

// Suspense fallback
const PageLoader = () => (
  <div className="loading">
    <div className="spinner"></div>
  </div>
);

// Turnstile challenge overlay (wired into AuthContext)
const ChallengeOverlay = () => {
  const { challengeActive, handleChallengeVerified, setChallengeActive } = useAuth();
  if (!challengeActive) return null;
  return (
    <TurnstileChallenge
      onVerified={handleChallengeVerified}
      onDismiss={() => setChallengeActive(false)}
    />
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <ChallengeOverlay />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />

              {/* Change password - protected but no role restriction */}
              <Route
                path="/change-password"
                element={
                  <ProtectedRoute>
                    <ChangePassword />
                  </ProtectedRoute>
                }
              />

              {/* Protected routes with layout */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Dashboard />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              {/* Customer routes - all roles */}
              <Route
                path="/customers"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <CustomerList />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/customers/new"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <CustomerForm />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/customers/:id"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <CustomerDetail />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/customers/:id/edit"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <CustomerForm />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              {/* Service routes - all roles */}
              <Route
                path="/services"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <ServiceList />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/services/new"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <ServiceForm />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              {/* Payment routes - manager+ */}
              <Route
                path="/payments"
                element={
                  <ProtectedRoute requireRole={['superadmin', 'admin', 'manager']}>
                    <MainLayout>
                      <PaymentList />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              {/* Inventory routes - manager+ */}
              <Route
                path="/inventory"
                element={
                  <ProtectedRoute requireRole={['superadmin', 'admin', 'manager']}>
                    <MainLayout>
                      <InventoryList />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              {/* Reports routes - all roles can access (page handles sub-permissions) */}
              <Route
                path="/reports"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Reports />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              {/* User routes - admin+ */}
              <Route
                path="/users"
                element={
                  <ProtectedRoute requireRole={['superadmin', 'admin']}>
                    <MainLayout>
                      <UserList />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              {/* Default redirect */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Suspense>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
